import imageCompression from 'browser-image-compression';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircle, Camera, Check, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import Webcam from 'react-webcam';

interface WebcamCaptureProps {
  onCapture: (imageData: string) => void;
  onCancel?: () => void;
  countdown?: number;
}

export function WebcamCapture({ onCapture, countdown = 0 }: WebcamCaptureProps) {
  const webcamRef = useRef<Webcam>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countdownValue, setCountdownValue] = useState<number | null>(null);

  const videoConstraints = {
    width: 640,
    height: 480,
    facingMode: 'user',
  };

  const capture = useCallback(async () => {
    if (!webcamRef.current) return;

    setIsCapturing(true);
    setError(null);

    try {
      const imageSrc = webcamRef.current.getScreenshot();
      if (!imageSrc) {
        throw new Error('Failed to capture image');
      }

      const response = await fetch(imageSrc);
      const blob = await response.blob();
      const file = new File([blob], 'capture.jpg', { type: 'image/jpeg' });

      const compressedFile = await imageCompression(file, {
        maxSizeMB: 0.5,
        maxWidthOrHeight: 640,
        useWebWorker: true,
      });

      const reader = new FileReader();
      const compressedBase64 = await new Promise<string>((resolve, reject) => {
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(compressedFile);
      });

      setCapturedImage(compressedBase64);
    } catch (err: any) {
      setError(err.message || 'Failed to capture image');
    } finally {
      setIsCapturing(false);
    }
  }, []);

  const startCountdown = useCallback(() => {
    if (countdown > 0) {
      setCountdownValue(countdown);
    } else {
      capture();
    }
  }, [countdown, capture]);

  useEffect(() => {
    if (countdownValue !== null && countdownValue > 0) {
      const timer = setTimeout(() => {
        setCountdownValue(countdownValue - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (countdownValue === 0) {
      capture();
      setCountdownValue(null);
    }
  }, [countdownValue, capture]);

  const retake = useCallback(() => {
    setCapturedImage(null);
    setError(null);
  }, []);

  const confirm = useCallback(() => {
    if (capturedImage) {
      onCapture(capturedImage);
    }
  }, [capturedImage, onCapture]);

  const handleUserMediaError = useCallback((error: string | DOMException) => {
    const message = typeof error === 'string' ? error : error.message;
    setError(`Camera access denied: ${message}`);
  }, []);

  return (
    <div className="w-full max-w-lg mx-auto space-y-6">
      {error && (
        <div className="p-4 rounded-2xl bg-rose-50 dark:bg-rose-900/20 border border-rose-100 dark:border-rose-800 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-rose-500" />
          <p className="text-sm text-rose-700 dark:text-rose-300 font-bold">{error}</p>
        </div>
      )}

      <div className="relative aspect-video bg-neolab-deep-green rounded-[2rem] overflow-hidden shadow-2xl border-4 border-white dark:border-neolab-forest">
        {capturedImage ? (
          <img src={capturedImage} alt="Captured" className="w-full h-full object-cover" />
        ) : (
          <>
            <Webcam
              ref={webcamRef}
              audio={false}
              screenshotFormat="image/jpeg"
              videoConstraints={videoConstraints}
              onUserMediaError={handleUserMediaError}
              className="w-full h-full object-cover"
            />
            <AnimatePresence>
              {countdownValue !== null && (
                <motion.div
                  initial={{ opacity: 0, scale: 2 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.5 }}
                  className="absolute inset-0 flex items-center justify-center bg-neolab-forest/40 backdrop-blur-sm"
                >
                  <span className="text-8xl font-black text-white drop-shadow-2xl">
                    {countdownValue}
                  </span>
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}

        {isCapturing && (
          <div className="absolute inset-0 flex items-center justify-center bg-neolab-forest/60 backdrop-blur-md">
            <div className="w-12 h-12 border-4 border-white/20 border-t-white rounded-full animate-spin" />
          </div>
        )}
      </div>

      <div className="flex justify-center gap-4">
        {!capturedImage ? (
          <button
            type="button"
            onClick={startCountdown}
            disabled={isCapturing || countdownValue !== null}
            className="flex items-center gap-3 px-10 py-4 rounded-2xl bg-neolab-green text-white font-black text-lg shadow-xl shadow-green-500/20 hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50"
          >
            <Camera className="w-6 h-6" />
            {countdown > 0 ? 'Start Timer' : 'Capture Now'}
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={retake}
              className="flex items-center gap-3 px-8 py-4 rounded-2xl bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-black text-lg hover:bg-slate-200 transition-all"
            >
              <RefreshCw className="w-5 h-5" />
              Retake
            </button>
            <button
              type="button"
              onClick={confirm}
              className="flex items-center gap-3 px-8 py-4 rounded-2xl bg-neolab-green text-white font-black text-lg shadow-xl shadow-green-500/20 hover:scale-[1.02] active:scale-95 transition-all"
            >
              <Check className="w-6 h-6" />
              Confirm
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default WebcamCapture;
